/**
 * VENDORED from tc-console/src/home/navMenuItems.tsx — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * The Home nav menu's item renderers — one implementation of folder headers,
 * indentation and the current-page mark, so every consumer's menu can never
 * drift. The destinations themselves always arrive as data — the Home doc's
 * `nav` (see HomeSurface) — never from a list kept here.
 *
 * Two treatments, one contract:
 *
 *   renderNavMenuItems       the compact overflow dropdown (HomeSurface's own
 *                            nav row): folder rows are section headers, page
 *                            rows navigate, the publish date rides every page
 *                            item (Douglas 2026-09-20: every document shows
 *                            it; the folder orders newest-first server-side).
 *                            UNCHANGED — the console's nav row keeps it.
 *   renderNavAccordionItems  the approved Explore panel design (the Hatchery
 *                            2026-09-24 mock, "Explore Menu — Hierarchical
 *                            Navigation"): every row carries a leading icon,
 *                            folders are INTERACTIVE accordion rows — the
 *                            chevron expands/collapses, the label follows the
 *                            folder's own page when it has one — children
 *                            indent along a guide line, and the current
 *                            page's ancestors arrive expanded
 *                            (navAccordionInitialKeys) with their own quiet
 *                            mark. The dates stay: Douglas's ruling stands.
 *
 * The style fragments below are exported so consumers whose panels mix Home
 * rows with their OWN row types (the Learner View header adds its surfaces
 * and nav folders) paint every row from the same palette — a separate row
 * style is exactly the drift this file exists to prevent.
 */

import type { ReactNode } from 'react'
import {
  alpha, Box, Collapse, ListItemIcon, MenuItem, Typography, type Theme,
} from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DescriptionIcon from '@mui/icons-material/Description'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import type { HomeNavLink } from './types'

export function renderNavMenuItems(
  items: HomeNavLink[],
  /** The page Home is currently showing — its own entry marks itself and
   *  re-selecting it is a no-op, exactly as in HomeSurface's nav row. */
  currentFile: string | undefined,
  onSelect: (n: HomeNavLink, active: boolean) => void,
  depth = 0,
): ReactNode[] {
  return items.flatMap((n) => {
    if (n.children?.length) {
      return [
        <MenuItem key={`h-${depth}-${n.label}`} disabled divider
          sx={{ pl: 1.5 + depth * 2, fontWeight: 700, fontSize: '0.75rem', opacity: 1, '&.Mui-disabled': { opacity: 0.7 } }}>
          {n.label}
        </MenuItem>,
        ...renderNavMenuItems(n.children, currentFile, onSelect, depth + 1),
      ]
    }
    const active = !!n.file && n.file === currentFile
    return [
      <MenuItem key={n.file ?? `${n.href}|${n.label}`} selected={active}
        sx={{ pl: 1.5 + depth * 2 }}
        onClick={() => onSelect(n, active)}>
        <Typography variant="body2">{n.label}</Typography>
        {/* The publish date rides every page item (Douglas 2026-09-20: every
            document shows it; the folder orders newest-first server-side). */}
        {n.date && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 2, flexShrink: 0 }}>
            {n.date}
          </Typography>
        )}
      </MenuItem>,
    ]
  })
}

// ── The accordion treatment (the approved Explore panel design) ─────────────

/** Every row: inset (never full-bleed), brand-rounded, 40px tall. */
export const navAccordionRowSx: SystemStyleObject<Theme> = {
  mx: 1,
  px: 1.5,
  minHeight: 40,
  py: 0.75,
  borderRadius: (t) => `${t.shape.borderRadius}px`,
}

/** The current page: the design's tinted pill in the brand primary. */
export const navAccordionSelectedSx: SystemStyleObject<Theme> = {
  '&.Mui-selected': {
    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
    '& .nav-accordion-label': { fontWeight: 600 },
  },
}

/** An ancestor of the current page: the design's quieter parent mark. */
export const navAccordionAncestorSx: SystemStyleObject<Theme> = {
  bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
  '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.09) },
  '& .nav-accordion-label': { fontWeight: 600 },
}

/** The leading icon slot (16px glyph, snug to the label). */
export const navAccordionIconSx: SystemStyleObject<Theme> = {
  minWidth: 28,
  color: 'text.secondary',
  '& .MuiSvgIcon-root': { fontSize: 16 },
}

/** The trailing date — the same right-aligned caption the compact menu has
 *  always carried (Douglas 2026-09-20). */
export const navAccordionDateSx: SystemStyleObject<Theme> = {
  ml: 'auto',
  pl: 1.5,
  flexShrink: 0,
}

/** A children group: indented one level, the guide line threading through the
 *  PARENT row's icon center. Rows are mx 8 + px 12 with a 28px icon slot, so
 *  the center is a constant 34px at every level (nested groups offset the
 *  rows AND this line together); consumers with a different row rhythm pass
 *  their own center (the LV drawer's roomier rows use 36). */
export function navAccordionGuideSx(iconCenter = 34): SystemStyleObject<Theme> {
  return {
    ml: `${iconCenter}px`,
    borderLeft: 1,
    borderColor: 'divider',
  }
}

/** A folder row's stable expansion key: its own page file when it has one,
 *  else its label path (unique among siblings in practice). */
export function navAccordionKey(n: HomeNavLink, parentKey: string): string {
  return n.file ?? `${parentKey}/${n.label}`
}

/** True when the current page lives anywhere in this subtree. */
export function navAccordionContainsCurrent(items: HomeNavLink[], currentFile: string | undefined): boolean {
  return items.some((n) =>
    (!!n.file && n.file === currentFile) || (!!n.children && navAccordionContainsCurrent(n.children, currentFile)),
  )
}

/** The folder keys on the current page's ancestor path — the expanded set the
 *  panel opens with, so the current page is always visible (the design's
 *  auto-expand rule). */
export function navAccordionInitialKeys(
  items: HomeNavLink[],
  currentFile: string | undefined,
): string[] {
  const keys: string[] = []
  const walk = (list: HomeNavLink[], parentKey: string): boolean =>
    list.some((n) => {
      const key = navAccordionKey(n, parentKey)
      if (n.children?.length) {
        if (walk(n.children, key)) {
          keys.push(key)
          return true
        }
        return !!n.file && n.file === currentFile
      }
      return !!n.file && n.file === currentFile
    })
  walk(items, '')
  return keys
}

export interface NavAccordionState {
  /** The expanded folder keys (navAccordionKey). */
  expanded: ReadonlySet<string>
  onToggle: (key: string) => void
}

/** The accordion renderer. Folder rows are interactive: the chevron (and, for
 *  a page-less folder, the whole row) expands/collapses the children; a folder
 *  that IS a page navigates from its label and expands from its chevron.
 *  ArrowRight/ArrowLeft on a focused folder row expand/collapse it — the
 *  treeview keyboard pattern inside the menu's own arrow-key navigation. */
export function renderNavAccordionItems(
  items: HomeNavLink[],
  currentFile: string | undefined,
  onSelect: (n: HomeNavLink, active: boolean) => void,
  accordion: NavAccordionState,
  depth = 0,
  parentKey = '',
): ReactNode[] {
  return items.flatMap((n) => {
    const key = navAccordionKey(n, parentKey)
    if (n.children?.length) {
      const isExpanded = accordion.expanded.has(key)
      const ancestor = navAccordionContainsCurrent(n.children, currentFile)
      const active = !!n.file && n.file === currentFile
      const regionId = `nav-acc-${key.replace(/[^a-z0-9_-]/gi, '_')}`
      const chevron = isExpanded
        ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        : <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      const folderRow = (
        <MenuItem
          key={`f-${key}`}
          selected={active}
          aria-expanded={isExpanded}
          aria-controls={regionId}
          sx={[navAccordionRowSx, navAccordionSelectedSx, ...(ancestor && !active ? [navAccordionAncestorSx] : [])]}
          onClick={() => (n.file ? onSelect(n, active) : accordion.onToggle(key))}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' && !isExpanded) { e.stopPropagation(); accordion.onToggle(key) }
            if (e.key === 'ArrowLeft' && isExpanded) { e.stopPropagation(); accordion.onToggle(key) }
          }}
        >
          <ListItemIcon sx={navAccordionIconSx}>
            {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
          </ListItemIcon>
          <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1 }}>
            {n.label}
          </Typography>
          {n.date && (
            <Typography variant="caption" color="text.secondary" sx={navAccordionDateSx}>
              {n.date}
            </Typography>
          )}
          {n.file ? (
            // A folder that is ALSO a page: the label navigates, the chevron
            // alone expands — two actions on one row, the design's split.
            <Box
              component="span"
              role="button"
              aria-label={isExpanded ? `Collapse ${n.label}` : `Expand ${n.label}`}
              onClick={(e) => { e.stopPropagation(); accordion.onToggle(key) }}
              sx={{ display: 'inline-flex', p: 0.5, ml: 0.5, borderRadius: 1 }}
            >
              {chevron}
            </Box>
          ) : chevron}
        </MenuItem>
      )
      return [
        folderRow,
        <Collapse key={`c-${key}`} in={isExpanded} timeout="auto" unmountOnExit>
          <Box id={regionId} sx={navAccordionGuideSx()}>
            {renderNavAccordionItems(n.children, currentFile, onSelect, accordion, depth + 1, key)}
          </Box>
        </Collapse>,
      ]
    }
    const active = !!n.file && n.file === currentFile
    return [
      <MenuItem
        key={n.file ?? `${n.href}|${n.label}`}
        selected={active}
        sx={[navAccordionRowSx, navAccordionSelectedSx]}
        onClick={() => onSelect(n, active)}
      >
        <ListItemIcon sx={navAccordionIconSx}>
          <DescriptionIcon />
        </ListItemIcon>
        <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1 }}>
          {n.label}
        </Typography>
        {n.date && (
          <Typography variant="caption" color="text.secondary" sx={navAccordionDateSx}>
            {n.date}
          </Typography>
        )}
      </MenuItem>,
    ]
  })
}
